// Normalized, presentation-only task shape (ADR-juuplan-tasks-clickup-overhaul D2).
//
// Every task surface — project tasks, the cross-project "Meine Aufgaben" hub, and
// personal Lists — maps its native rows into a `TaskRecord` so the List/Board/Calendar
// views can render them through one code path. The `raw` escape hatch keeps the
// original row available for source-specific UI (e.g. the project TaskModal).
//
// Pure module: no network, no stores. Mappers take everything they need as args.

import type {
  LabelItem,
  PersonalTask,
  Phase,
  StatusCategory,
  Task,
  TaskPriority,
} from "@juuplan/shared";
import { personalAssigneeIds } from "./personalAssignees";

export type TaskSource = "project" | "personal";

/** Source-agnostic status descriptor carried on every record. */
export interface RecordStatus {
  /** Project custom-status id; null for personal tasks (no status table). */
  id: number | null;
  label: string;
  /** CSS color — a `var(--…)` token or a hex string. */
  color: string;
  category: StatusCategory;
}

/**
 * Normalized task for the unified view layer. Field names are camelCase
 * (presentation layer) regardless of the snake_case API rows they derive from.
 */
export interface TaskRecord {
  source: TaskSource;
  /** Native id within the source table. */
  id: number;
  /** Stable id unique across sources, e.g. "project:123" / "personal:45". */
  uid: string;
  title: string;
  /** Ticket key `#<seq>` (project tasks); null for personal tasks / unnumbered. */
  ticket: string | null;
  /** True when the task is in a done/completed state. */
  done: boolean;
  status: RecordStatus;
  priority: TaskPriority | null;
  assigneeIds: number[];
  primaryAssigneeId: number | null;
  startDate: string | null;
  /** The date a row sorts/groups by: project deadline ?? end_date; personal due_date. */
  dueDate: string | null;
  isMilestone: boolean;
  labelIds: number[];
  /** Project context — null for personal tasks. */
  projectId: number | null;
  phaseId: number | null;
  /**
   * Work-item type id (project tasks only); null for personal tasks and
   * untyped project tasks. Optional (not just nullable) so TaskRecord literals
   * elsewhere in the codebase that predate this field (outside this ticket's
   * scope to touch) keep typechecking without it — always treat a missing
   * value the same as null (`r.typeId ?? null`).
   */
  typeId?: number | null;
  /** Personal-list context — null for project tasks. Drives group-by-list. */
  listId: number | null;
  /** Parent task id for subtask nesting (project only); null otherwise. */
  parentId: number | null;
  /** Count of direct child subtasks (project only); 0 otherwise. */
  subtaskCount: number;
  /** Manual ordering within its source. */
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  /** Checklist progress (project subtasks / personal steps). 0/0 when none. */
  checklistDone: number;
  checklistTotal: number;
  /** Original row for source-specific affordances. */
  raw: Task | PersonalTask;
}

/**
 * Per-source resolver + mutator seam. Views never reach into project-global
 * stores directly — they ask the context to resolve ids and to apply edits,
 * so the right API call happens per source (project vs. personal).
 */
export interface ResolverContext {
  resolveProject(id: number): { name: string; color: string | null } | undefined;
  resolvePhase(id: number): Phase | undefined;
  resolveLabel(id: number): LabelItem | undefined;
  resolveUserName(id: number): string;
  /** Apply a normalized change to a record; resolves when persisted. */
  patch(record: TaskRecord, change: TaskRecordChange): Promise<void>;
  /**
   * The ordered, colored status set applicable to a record — the record's list
   * statuses for personal tasks, its project statuses for project tasks. Feeds
   * inline status dropdowns and status-grouped buckets. Optional: when absent,
   * the row shows the current status without a picker.
   */
  resolveStatuses?(record: TaskRecord): StatusOption[];
  /** Resolve a personal list's display name/color (group-by-list headers). */
  resolveList?(id: number): { name: string; color: string | null } | undefined;
  /** Apply one change to many records at once (bulk toolbar). */
  patchMany?(records: TaskRecord[], change: TaskRecordChange): Promise<void>;
  /** Delete many records at once (bulk toolbar). */
  deleteMany?(records: TaskRecord[]): Promise<void>;
}

/** A selectable status option (list or project scoped). */
export interface StatusOption {
  id: number;
  name: string;
  color: string;
  category: StatusCategory;
}

/** Normalized edit payload — the context translates it to the source's API. */
export interface TaskRecordChange {
  title?: string;
  done?: boolean;
  statusId?: number | null;
  priority?: TaskPriority | null;
  assigneeIds?: number[];
  startDate?: string | null;
  dueDate?: string | null;
}

// ---------- mappers ----------

/** Project-task "done" test: custom status category wins, legacy enum as fallback. */
function projectTaskDone(t: Task): boolean {
  if (t.statusInfo) return t.statusInfo.category === "done" || t.statusInfo.category === "cancelled";
  return t.status === "done";
}

/**
 * Map a project task into a TaskRecord. `statusInfo` (custom status) is the
 * source of truth for status display; falls back to a neutral backlog descriptor
 * when the join is unavailable.
 */
export function projectTaskToRecord(t: Task): TaskRecord {
  const status: RecordStatus = t.statusInfo
    ? {
        id: t.statusInfo.id,
        label: t.statusInfo.name,
        color: t.statusInfo.color,
        category: t.statusInfo.category,
      }
    : {
        id: null,
        label: "Backlog",
        color: "var(--color-outline)",
        category: "backlog",
      };
  return {
    source: "project",
    id: t.id,
    uid: `project:${t.id}`,
    title: t.title,
    ticket: t.ticket ?? null,
    done: projectTaskDone(t),
    status,
    priority: t.priority,
    assigneeIds: t.assignee_ids ?? [],
    primaryAssigneeId: t.primary_assignee_id ?? null,
    startDate: t.start_date,
    dueDate: t.deadline ?? t.end_date,
    isMilestone: t.is_milestone,
    labelIds: t.label_ids ?? [],
    projectId: null, // filled by caller when known (Task has no project_id field)
    phaseId: t.phase_id,
    typeId: t.type_id ?? null,
    listId: null,
    parentId: t.parent_id ?? null,
    subtaskCount: t.subtask_count ?? 0,
    sortOrder: t.sort_order,
    createdAt: t.created_at,
    updatedAt: t.updated_at,
    checklistDone: 0,
    checklistTotal: t.subtask_count ?? 0,
    raw: t,
  };
}

/**
 * Map a project task that has been annotated with its project id (e.g. the
 * `/api/me/tasks` payload joins project_id/phase_id). Falls through to
 * `projectTaskToRecord` and stamps the project id.
 */
export function projectTaskToRecordWithProject(
  t: Task,
  projectId: number
): TaskRecord {
  const rec = projectTaskToRecord(t);
  rec.projectId = projectId;
  return rec;
}

/**
 * Personal task status: the configurable per-list status (status_info) is the
 * source of truth once present; falls back to a synthesized is_done descriptor
 * for inbox tasks (no list → no status set) and older payloads.
 */
function personalStatus(pt: PersonalTask): RecordStatus {
  if (pt.status_info) {
    return {
      id: pt.status_info.id,
      label: pt.status_info.name,
      color: pt.status_info.color,
      category: pt.status_info.category,
    };
  }
  return pt.is_done
    ? { id: null, label: "Erledigt", color: "var(--color-status-done)", category: "done" }
    : { id: null, label: "Offen", color: "var(--color-outline)", category: "unstarted" };
}

/** Map a personal task into a TaskRecord. */
export function personalTaskToRecord(pt: PersonalTask): TaskRecord {
  const personalAssignees = personalAssigneeIds(pt);
  return {
    source: "personal",
    id: pt.id,
    uid: `personal:${pt.id}`,
    title: pt.title,
    ticket: null,
    done: pt.is_done,
    status: personalStatus(pt),
    priority: pt.priority,
    // #854: personal tasks carry a SET of assignees now. personalAssigneeIds
    // kapselt den Vorrang — `assignee_ids: []` heisst "niemand", auch wenn im
    // selben Payload noch ein assignee_id steht.
    assigneeIds: personalAssignees,
    primaryAssigneeId: personalAssignees[0] ?? null,
    startDate: null,
    dueDate: pt.due_date,
    isMilestone: false,
    labelIds: [],
    projectId: null,
    phaseId: null,
    typeId: null, // personal tasks have no work-item type table
    listId: pt.list_id ?? null,
    parentId: null,
    subtaskCount: 0,
    sortOrder: pt.sort_order,
    createdAt: pt.created_at,
    updatedAt: pt.updated_at,
    checklistDone: pt.step_done_count ?? 0,
    checklistTotal: pt.step_count ?? 0,
    raw: pt,
  };
}

// ---------- date write-back ----------

/** Project-task date fields a normalized date change may touch. */
export interface ProjectDatePatch {
  start_date?: string | null;
  end_date?: string | null;
  deadline?: string | null;
}

/**
 * Translate a normalized `dueDate`/`startDate` change into the project-task
 * date fields to PATCH.
 *
 * A record's `dueDate` is READ as `deadline ?? end_date`
 * (`projectTaskToRecord`), so the write-back has to hit exactly the field that
 * produced the value on screen. Writing `end_date` on a task that carries a
 * `deadline` would persist fine and still leave the row showing the old date —
 * indistinguishable from a lost edit.
 *
 * The Start≤Ende rule is NOT applied here: the server pulls the non-edited
 * side along by itself (`buildTaskPatch`).
 */
export function projectDateChangeToPatch(
  t: Pick<Task, "deadline" | "start_date" | "end_date">,
  change: Pick<TaskRecordChange, "dueDate" | "startDate">
): ProjectDatePatch {
  const patch: ProjectDatePatch = {};
  if (change.startDate !== undefined) patch.start_date = change.startDate;
  if (change.dueDate !== undefined) {
    if (t.deadline) {
      patch.deadline = change.dueDate;
    } else if (
      change.startDate === undefined &&
      t.start_date &&
      t.end_date &&
      t.start_date === t.end_date
    ) {
      // Zero-duration span (start === end, no deadline): keep both ends in
      // sync, otherwise the task silently becomes a range. When the caller
      // moves the start itself (calendar range drag) it owns both fields.
      patch.start_date = change.dueDate;
      patch.end_date = change.dueDate;
    } else if (t.end_date) {
      patch.end_date = change.dueDate;
    } else {
      // Undated task — `deadline` is the semantic due date and is what the
      // row will read back.
      patch.deadline = change.dueDate;
    }
  }
  return patch;
}
